import { useCallback, useEffect, useRef } from 'react'

type TurnstileRenderOptions = {
  sitekey: string
  appearance?: 'always' | 'execute' | 'interaction-only'
  callback?: (token: string) => void
  'expired-callback'?: () => void
  'error-callback'?: () => void
}

export type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const TOKEN_WAIT_MS = 15000

let loaderPromise: Promise<TurnstileApi> | null = null

// Lazily loads the Turnstile script the first time a newsletter form needs it.
function loadTurnstile(): Promise<TurnstileApi> {
  if (loaderPromise) return loaderPromise

  loaderPromise = new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile)
      return
    }

    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => {
      if (window.turnstile) {
        resolve(window.turnstile)
      } else {
        reject(new Error('Turnstile script loaded but API is missing'))
      }
    }
    script.onerror = () => {
      loaderPromise = null
      reject(new Error('Failed to load Turnstile script'))
    }
    document.head.appendChild(script)
  })

  return loaderPromise
}

/**
 * Mounts an invisible (interaction-only) Turnstile widget into the returned
 * ref's element and hands out tokens for form submits.
 *
 * With a null site key (unconfigured dev) the hook is a no-op: getToken()
 * resolves undefined and the server skips verification when its secret is
 * also absent.
 */
export function useTurnstile(siteKey: string | null, enabled = true) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const tokenRef = useRef<string | null>(null)
  const waitersRef = useRef<Array<(token: string) => void>>([])

  useEffect(() => {
    if (!siteKey || !enabled) return

    let cancelled = false

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          callback: (token) => {
            tokenRef.current = token
            waitersRef.current.forEach((resolve) => resolve(token))
            waitersRef.current = []
          },
          'expired-callback': () => {
            tokenRef.current = null
          },
          'error-callback': () => {
            tokenRef.current = null
          },
        })
      })
      .catch(() => {
        // Script blocked or offline: submit proceeds without a token and the
        // server decides.
      })

    return () => {
      cancelled = true
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile?.remove(widgetIdRef.current)
        } catch {
          // Widget already gone (e.g. dialog unmounted its DOM first).
        }
        widgetIdRef.current = null
        tokenRef.current = null
        waitersRef.current = []
      }
    }
  }, [siteKey, enabled])

  const getToken = useCallback((): Promise<string | undefined> => {
    if (!siteKey) return Promise.resolve(undefined)
    if (tokenRef.current) return Promise.resolve(tokenRef.current)

    // Wait for the widget's callback rather than submitting without a token;
    // cap the wait so the form never hangs if the script was blocked.
    return new Promise((resolve) => {
      const timer = window.setTimeout(() => {
        waitersRef.current = waitersRef.current.filter((w) => w !== onToken)
        resolve(undefined)
      }, TOKEN_WAIT_MS)
      const onToken = (token: string) => {
        window.clearTimeout(timer)
        resolve(token)
      }
      waitersRef.current.push(onToken)
    })
  }, [siteKey])

  const reset = useCallback(() => {
    tokenRef.current = null
    if (widgetIdRef.current !== null) {
      try {
        window.turnstile?.reset(widgetIdRef.current)
      } catch {
        // Widget already removed.
      }
    }
  }, [])

  return { containerRef, getToken, reset }
}
